import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Modal, Table, StatusPill, EmptyState,
} from '../../../design-system';
import { money, day } from '../../../lib/tallyBooks';
import '../../styles/TallyPanel.css';

// One ledger's whole account, in a layer over whatever list you clicked from.
//
// ⚠️ THIS IS THE ANSWER TO "AND THEN WHAT?". Every list in this module named a
// party and stopped: the matching queue showed a name, a group and a GSTIN; the
// ledger list added an opening and a closing balance. All of them beg the same
// question — what actually went through this account — and none of them could
// answer it, even though every voucher was already mirrored and sitting in the
// database. So a preparer read a closing balance of ₹4,82,000 with no way to
// see the six invoices and two receipts that made it.
//
// ⚠️ A LAYER, NOT A PAGE, AND THAT IS THE POINT. Clicking a party is a question
// asked in passing while working down a list — of forty debtors, which one is
// this. Navigating away loses the list, its scroll position and its filters,
// and makes the reader pay to come back to where they already were. The layer
// closes onto exactly what was underneath.
//
// ⚠️ DEBIT AND CREDIT COLUMNS, NEVER A SIGNED AMOUNT. See the header of
// server-side utils/tally/statement.js: Tally's two sign conventions are
// opposite to each other, so a signed column is a column half its readers will
// read backwards. The server decides direction from Tally's own flag; this
// draws the two columns it returns.

/** A balance in the accounting convention: credit in parentheses. */
function Balance({ value, strong }) {
  if (value == null) return <span className="tally-num">—</span>;
  const n = Number(value) || 0;
  const text = n < 0 ? `(${money(Math.abs(n))})` : money(n);
  return (
    <span className={`tally-num${n < 0 ? ' tally-num-cr' : ''}${strong ? ' tally-num-strong' : ''}`}>
      {text}
    </span>
  );
}

/** A movement column. ⚠️ Nil prints blank, not "0.00" — a debit-only row must
 *  read at a glance, and a column of zeros beside every figure defeats that. */
const Move = ({ value }) => (
  <span className="tally-num">{value ? money(value) : ''}</span>
);

function TallyLedgerStatement({ connectionId, ledgerId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get(
        `/api/tally/connections/${connectionId}/ledgers/${ledgerId}/statement`,
      );
      setData(res.data || null);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read that ledger');
    } finally {
      setLoading(false);
    }
  }, [connectionId, ledgerId]);

  useEffect(() => { load(); }, [load]);

  const ledger = data?.ledger;
  const rec = data?.reconciliation;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={ledger?.name || 'Ledger'}
    >
      {loading && !data ? (
        <p className="tally-muted">Reading the account…</p>
      ) : error ? (
        <p className="tally-error">{error}</p>
      ) : !data ? null : (
        <>
          {/* Who this is. The identifiers are here because the commonest reason
              to open a party is to check you are looking at the right one. */}
          <div className="tally-statement-id">
            <span className="tally-muted">{ledger.parent || 'No group'}</span>
            {ledger.gstin && <span className="tally-chip">{ledger.gstin}</span>}
            {ledger.pan && <span className="tally-chip">{ledger.pan}</span>}
            {ledger.matchStatus === 'confirmed' && (
              <StatusPill variant="success" label="Matched to a client" />
            )}
          </div>

          <div className="tally-position-totals">
            <div>
              <span className="tally-kpi-label">Opening</span>
              <Balance value={ledger.openingBalance} strong />
            </div>
            <div>
              <span className="tally-kpi-label">Debits</span>
              <span className="tally-num tally-num-strong">{money(data.totals.debit)}</span>
            </div>
            <div>
              <span className="tally-kpi-label">Credits</span>
              <span className="tally-num tally-num-strong">{money(data.totals.credit)}</span>
            </div>
            <div>
              <span className="tally-kpi-label">Closing</span>
              <Balance value={ledger.closingBalance} strong />
            </div>
          </div>

          {/* ⚠️ THE BRIDGE IS ALWAYS DRAWN, not only when it breaks. This one
              line is the whole reason somebody opened the layer: the ledger
              list showed an opening and a closing figure and nothing that
              joined them. Stating the arithmetic — opening, plus what moved,
              equals closing — is the breakup, and the rows below are its
              detail. Shown even when it reconciles, because a reader has to be
              able to SEE that it does rather than assume it. */}
          {rec && (
            <p className={rec.reconciles ? 'tally-muted' : 'tally-bridge-off'}>
              <span className="tally-num">{money(rec.opening)}</span>
              {' opening '}
              {rec.movement < 0 ? '−' : '+'}
              <span className="tally-num">{money(Math.abs(rec.movement))}</span>
              {' moved by the '}
              {`${data.totals.count - data.totals.excluded} entr${data.totals.count - data.totals.excluded === 1 ? 'y' : 'ies'} below`}
              {' = '}
              <span className="tally-num">{money(rec.derivedClosing)}</span>
              {rec.reconciles
                ? ', which is the closing balance Tally reports.'
                : `, against the ${money(rec.reportedClosing)} Tally reports.`}
            </p>
          )}

          {/* ⚠️ THE SHORTFALL IS STATED OUT LOUD, and the ordinary cause is
              named first. A backfill still running is the usual reason the
              movements do not reach the closing balance, and it is not an
              error — but a reader totting up the column below and landing
              somewhere else deserves to know why before they start doubting the
              books. */}
          {rec && !rec.reconciles && (
            <p className="tally-warn">
              <strong>This account is not complete.</strong>{' '}
              {`${money(Math.abs(rec.difference))} of movement is missing between the entries below and the closing balance Tally holds. `}
              {rec.truncated
                ? 'This ledger has more entries than one screen holds, so the earliest are not shown.'
                : 'That usually means the historical backfill has not finished mirroring this ledger’s older vouchers yet.'}
            </p>
          )}

          {rec?.truncated && rec.reconciles && (
            <p className="tally-muted">
              {`Showing the ${data.totals.count} most recent of ${data.total} entries.`}
            </p>
          )}

          {!data.rows.length ? (
            <EmptyState
              icon="📄"
              title="No entries against this ledger"
              description="Nothing mirrored so far has touched this account. If the historical backfill is still running, older vouchers may not have arrived yet."
            />
          ) : (
            <div className="tally-statement-rows">
              <Table
                columns={[
                  { key: 'date', header: 'Date', render: (r) => day(r.date) },
                  {
                    key: 'particulars',
                    header: 'Particulars',
                    render: (r) => (
                      <div>
                        <div className="tally-statement-vch">
                          {r.voucherType || 'Entry'}
                          {r.voucherNumber && <span className="tally-muted">{` #${r.voucherNumber}`}</span>}
                          {/* ⚠️ MARKED, NOT HIDDEN. A cancelled voucher leaves a
                              hole in a numbered series, and a hole reads as lost
                              data to whoever is reconciling. It is shown, greyed,
                              and kept out of the totals by the server. */}
                          {r.isCancelled && <StatusPill variant="danger" label="Cancelled" />}
                          {r.isOptional && <StatusPill variant="warning" label="Optional" />}
                        </div>
                        {/* The other side of the entry — which party, and what
                            the person typed on it. This is what makes the row
                            recognisable as a real transaction rather than a
                            date and a figure. */}
                        {r.partyLedgerName && r.partyLedgerName !== ledger.name && (
                          <div className="tally-muted">{r.partyLedgerName}</div>
                        )}
                        {r.narration && <div className="tally-statement-narration">{r.narration}</div>}
                        {!!r.bills.length && (
                          <div className="tally-muted">
                            {`Against ${r.bills.map((b) => b.name).join(', ')}`}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  { key: 'debit', header: 'Debit', render: (r) => <Move value={r.debit} /> },
                  { key: 'credit', header: 'Credit', render: (r) => <Move value={r.credit} /> },
                  {
                    key: 'balance',
                    header: 'Balance',
                    render: (r) => <Balance value={r.runningBalance} />,
                  },
                ]}
                data={data.rows}
                keyField="id"
              />
            </div>
          )}

          <p className="tally-muted">
            {`${data.totals.count} entr${data.totals.count === 1 ? 'y' : 'ies'}`}
            {data.totals.excluded > 0 && `, of which ${data.totals.excluded} cancelled or optional and not counted`}
            {'. Every figure is Tally’s own, as at the last sync — nothing here is recomputed by us.'}
          </p>
        </>
      )}
    </Modal>
  );
}

export default TallyLedgerStatement;
